import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../../design-system/components/Card';
import { Badge } from '../../design-system/components/Decorative';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import { hexToRgba } from '../../utils/colorUtils';

const GroupedListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const GroupHeader = styled.button<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing[4]} ${spacing[5]};
  background: ${props => props.theme.colors.surfaceHover};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  cursor: pointer;
  transition: ${animations.transition(
    ['background-color', 'border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  text-align: left;

  &:hover {
    background: ${props => props.theme.colors.surface};
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const GroupHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex: 1;
`;

const GroupTitle = styled(Heading)`
  margin: 0;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
`;

const GroupCount = styled(Badge)`
  font-size: ${typography.fontSize.sm};
`;

const ChevronIcon = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.textSecondary};
  transition: ${animations.transition(
    ['transform', 'color'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  transform: ${props => (props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const GroupContent = styled.div<{ isExpanded: boolean }>`
  overflow: hidden;
  max-height: ${props => (props.isExpanded ? '10000px' : '0')};
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  transition: ${animations.transition(
    ['max-height', 'opacity', 'margin-top'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  margin-top: ${props => (props.isExpanded ? spacing[4] : 0)};
`;

const GroupItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${spacing[4]};
  padding: ${spacing[4]} 0;
`;

const EmptyGroupMessage = styled.div`
  padding: ${spacing[6]};
  text-align: center;
  color: ${props => props.theme.colors.textMuted};
  font-size: ${typography.fontSize.sm};
  font-style: italic;
`;

export interface GroupedListProps<T> {
  items: T[];
  groupBy: (item: T) => string;
  groupLabel: (key: string) => string;
  renderItem: (item: T) => React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: string[];
  emptyMessage?: string;
  className?: string;
}

export function GroupedList<T>({
  items,
  groupBy,
  groupLabel,
  renderItem,
  collapsible = true,
  defaultExpanded = [],
  emptyMessage,
  className,
}: GroupedListProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage: string =
    emptyMessage ?? ((t('common.noItemsInGroup') as string) || 'No items in this group');
  // Group items
  const groupedItems = useMemo(() => {
    const groups = new Map<string, T[]>();
    items.forEach(item => {
      const key = groupBy(item);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    });
    return groups;
  }, [items, groupBy]);

  // Track expanded state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(defaultExpanded)
  );

  // Initialize all groups as expanded if defaultExpanded is empty and collapsible is false
  React.useEffect(() => {
    if (!collapsible) {
      setExpandedGroups(new Set(Array.from(groupedItems.keys())));
    } else if (defaultExpanded.length === 0 && groupedItems.size > 0) {
      // Expand first group by default
      const firstKey = Array.from(groupedItems.keys())[0];
      setExpandedGroups(new Set([firstKey]));
    }
  }, [collapsible, defaultExpanded, groupedItems]);

  const toggleGroup = (key: string) => {
    if (!collapsible) return;
    
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isExpanded = (key: string) => {
    if (!collapsible) return true;
    return expandedGroups.has(key);
  };

  // Sort groups by key for consistent ordering
  const sortedGroups = Array.from(groupedItems.entries()).sort((a, b) => 
    groupLabel(a[0]).localeCompare(groupLabel(b[0]))
  );

  if (sortedGroups.length === 0) {
    return null;
  }

  return (
    <GroupedListContainer className={className}>
      {sortedGroups.map(([key, groupItems]) => {
        const expanded = isExpanded(key);
        const label = groupLabel(key);
        
        return (
          <div key={key}>
            <GroupHeader
              isExpanded={expanded}
              onClick={() => toggleGroup(key)}
              type="button"
              aria-expanded={expanded}
              aria-controls={`group-${key}`}
            >
              <GroupHeaderLeft>
                {collapsible && (
                  <ChevronIcon isExpanded={expanded}>
                    {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </ChevronIcon>
                )}
                <GroupTitle variant="h5" color="text">
                  {label}
                </GroupTitle>
                <GroupCount size="sm" color="primary" shape="pill">
                  {groupItems.length}
                </GroupCount>
              </GroupHeaderLeft>
            </GroupHeader>
            
            <GroupContent isExpanded={expanded} id={`group-${key}`}>
              {groupItems.length === 0 ? (
                <EmptyGroupMessage>{resolvedEmptyMessage}</EmptyGroupMessage>
              ) : (
                <GroupItemsContainer>
                  {groupItems.map((item, index) => (
                    <React.Fragment key={index}>
                      {renderItem(item)}
                    </React.Fragment>
                  ))}
                </GroupItemsContainer>
              )}
            </GroupContent>
          </div>
        );
      })}
    </GroupedListContainer>
  );
}

export default GroupedList;
